import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import { fetchBridgeTokenById } from '@/api/modules'
import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  buildInsertTokenMessage,
  InsertTokenFormValues,
} from './tokenMessageBuilder'

const insertTokenSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  token: z.object({
    id: z
      .string()
      .min(1, 'Token ID is required')
      .refine(
        async tokenId => {
          try {
            await fetchBridgeTokenById(tokenId)
            return false
          } catch {
            return true
          }
        },
        { message: 'Token with this ID already exists' },
      ),
    metadata: z.object({
      name: z.string().min(1, 'Token name is required'),
      symbol: z.string().min(1, 'Symbol is required'),
      uri: z.string().url(),
      dexName: z.string().optional(),
    }),
    info: z.array(z.any()).default([]),
  }),
})

export default function InsertTokenForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<InsertTokenFormValues>({
    resolver: zodResolver(insertTokenSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      token: {
        id: '',
        metadata: {
          name: '',
          symbol: '',
          uri: '',
          dexName: '',
        },
        info: [],
      },
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildInsertTokenMessage(watchedValues as InsertTokenFormValues)
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Insert Token Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to generate a message for inserting a new token
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
            {...form.register('creator')}
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>Token Details</h4>

          <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='id'>Token ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='id'
                  placeholder='1'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                  {...form.register('token.id')}
                />
              </GradientWrapper>
              {form.formState.errors.token?.id && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.token.id.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='name'>Token Name</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='name'
                  placeholder='Ethereum'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                  {...form.register('token.metadata.name')}
                />
              </GradientWrapper>
              {form.formState.errors.token?.metadata?.name && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.token.metadata.name.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='symbol'>Symbol</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='symbol'
                  placeholder='ETH'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                  {...form.register('token.metadata.symbol')}
                />
              </GradientWrapper>
              {form.formState.errors.token?.metadata?.symbol && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.token.metadata.symbol.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='uri'>URI</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='uri'
                  placeholder='https://...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                  {...form.register('token.metadata.uri')}
                />
              </GradientWrapper>
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='dexName'>DEX Name (Optional)</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='dexName'
                  placeholder='Uniswap'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                  {...form.register('token.metadata.dexName')}
                />
              </GradientWrapper>
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'insert-token-message.json')
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildInsertTokenMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
